import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Backdrop,
  BaseTextFieldController,
  DatePicker,
  MediaUploadZone,
  Form,
  FormAttachments,
  FormGroup,
  RichEditorController,
  SelectController,
  FormCancelButton,
  FormSubmitButton,
  NewConfirmDialog,
} from 'components';
import { POLL_FORM_FIELDS_LENGTH } from 'constants/fieldsLength';
import { multiSelectStyles } from 'features/documentCategories/components/CreateDocumentCategoriesForm/styles';
import { FormContainer } from 'features/tasks/components/TaskForm/styled';
import { useWindowSize } from 'hooks';
import { textFieldStyles, textFieldMobileStyles, richEditorStyles } from 'styles';
import { PollFormValues, PostDraft } from 'types';

import { initialWindowSizeProps } from '../constants';
import { OptionsFields } from '../OptionsFields';
import { AllOption } from './constants';
import { usePollForm } from './hooks';
import { LabelTitle } from './styled';
import { descriptionFieldRules, titleFieldRules, visibilityFieldRules } from './validation';

interface PollFormProps {
  isLoading: boolean;
  defaultValues: (draft?: PostDraft) => PollFormValues;
  onSubmit: (data: PollFormValues, attachmentIds: string[]) => void;
  draft?: PostDraft | null;
  mode?: 'create' | 'edit';
  onClose?: () => void;
}

export function PollForm({
  isLoading: isPollMutationLoading,
  defaultValues,
  onSubmit,
  draft,
  mode = 'create',
  onClose,
}: PollFormProps): ReactElement {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize(initialWindowSizeProps);
  const isEditMode = mode === 'edit';

  const {
    isLoading,
    isSubmitting,
    isDirty,
    isSubmitted,
    isValidOptionsNumber,
    control,
    attachedImages,
    attachedDocs,
    attachedVideos,
    usersOptions,
    progress,
    getValues,
    handleFormSubmit,
    handleUploadAll,
    handleDeleteAttachment,
    handleAssigneeChange,
    isOpen,
    handleSubmitConfirmModal,
    handleCloseConfirmModal,
  } = usePollForm({
    draft,
    isPollMutationLoading,
    isEditMode,
    defaultValues,
    onSubmit,
  });

  const navigate = useNavigate();
  const isSubmitButtonDisabled = (isEditMode && !isDirty) || isSubmitting || isPollMutationLoading;
  const formGroupMarginBottom = isMobile ? 16 : 24;
  const fieldStyles = isMobile ? textFieldMobileStyles : textFieldStyles;

  return (
    <>
      {(isLoading || isSubmitting) && <Backdrop progress={progress} />}

      <FormContainer>
        <Form onSubmit={handleFormSubmit}>
          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('poll.create.form.placeholders.title')}</LabelTitle>
            <BaseTextFieldController
              name="title"
              control={control}
              placeholder={t('poll.create.form.placeholders.title')}
              sx={fieldStyles}
              maxLength={POLL_FORM_FIELDS_LENGTH.title.max}
              rules={titleFieldRules}
              disabled={isEditMode}
            />
          </FormGroup>

          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('poll.create.form.placeholders.visibility')}</LabelTitle>
            <SelectController
              name="visibility"
              control={control}
              placeholder={t('poll.create.form.placeholders.visibility')}
              options={[AllOption, ...usersOptions]}
              multipleSelect
              sx={multiSelectStyles(isMobile)}
              handleChange={handleAssigneeChange}
              type="poll"
              rules={visibilityFieldRules}
            />
          </FormGroup>

          <FormGroup marginBottom={formGroupMarginBottom}>
            <LabelTitle>{t('poll.create.form.placeholders.description')}</LabelTitle>
            <RichEditorController
              name="description"
              control={control}
              placeholder={t('poll.create.form.placeholders.description')}
              maxLength={POLL_FORM_FIELDS_LENGTH.description.max}
              rules={descriptionFieldRules}
              sx={richEditorStyles(isMobile)}
            />
          </FormGroup>

          <FormGroup marginBottom={formGroupMarginBottom} row>
            <LabelTitle>{t('poll.create.form.placeholders.deadline')}</LabelTitle>
            <DatePicker
              name="deadline"
              control={control}
              placeholder={t('poll.create.form.placeholders.deadline')}
              sx={fieldStyles}
            />
          </FormGroup>
          <FormGroup marginBottom={formGroupMarginBottom}>
            {!isMobile && (
              <FormAttachments
                attachedImages={attachedImages}
                attachedDocs={attachedDocs}
                attachedVideos={attachedVideos}
                handleDeleteAttachment={handleDeleteAttachment}
                draftId={draft?.id}
              />
            )}

            {isMobile && (
              <FormAttachments
                attachedImages={attachedImages}
                attachedDocs={attachedDocs}
                attachedVideos={attachedVideos}
                handleDeleteAttachment={handleDeleteAttachment}
                draftId={draft?.id}
              />
            )}
            <MediaUploadZone onUpload={(files) => handleUploadAll(files, draft?.id)} />
          </FormGroup>

          <OptionsFields
            control={control}
            getValues={getValues}
            isSubmitted={isSubmitted}
            isValidOptionsNumber={isValidOptionsNumber}
            disabled={isEditMode}
          />

          {!isMobile && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '12px',
                marginTop: '32px',
              }}
            >
              <FormCancelButton
                variant="outlined"
                onClick={() => (onClose ? onClose() : navigate(-1))}
              >
                {t('common.cancel')}
              </FormCancelButton>
              <FormSubmitButton type="submit" disabled={isSubmitButtonDisabled}>
                {isEditMode ? t('common.save') : t('common.create')}
              </FormSubmitButton>
            </div>
          )}
        </Form>

        {isMobile && (
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}
          >
            <FormCancelButton
              variant="outlined"
              onClick={() => (onClose ? onClose() : navigate(-1))}
            >
              {t('common.cancel')}
            </FormCancelButton>
            <FormSubmitButton disabled={isSubmitButtonDisabled} onClick={handleFormSubmit}>
              {isEditMode ? t('common.save') : t('common.create')}
            </FormSubmitButton>
          </div>
        )}
      </FormContainer>

      {isOpen && (
        <NewConfirmDialog
          title={t('task.create.modal.title')}
          subTitle={t('task.create.modal.exit')}
          open={isOpen}
          onClose={handleCloseConfirmModal}
          onConfirm={handleSubmitConfirmModal}
          submitLabel={t('task.create.modal.successButton')}
          cancelLabel={t('task.create.modal.cancelButton')}
        />
      )}
    </>
  );
}
